import { useQuery } from '@tanstack/vue-query'
import type { ClaimedRewardDetail } from '~/types/reward'
import type { Store, StorePage, StoreLocales } from '~/types/store'

export const useStoreStore = defineStore('store', () => {
  const scrollPosition = ref(0)
  const viewportHeight = ref(0)
  const appHeight = ref(0)
  const offset = ref(0)
  const modalHeight = ref(0)
  const modalTop = ref(0)

  const langCode = ref<string | null>(null)
  const langSuffix = ref<string | null>(null)
  const origin = ref<string | null>(null)

  const currencyCodeExtension = ref<string | null>(null)
  const currencyRate = ref<number>(1.0)

  const store = ref<Store | null>(null)

  const currencyCode = computed(() => {
    // currencyCodeExtension.value from shopify international extension (browsing user locale)
    // store.value?.currency_code is fallback (store locale)
    return currencyCodeExtension.value ?? store.value?.currency_code ?? 'EUR'
  })

  const isShopify = computed(() => store.value?.provider === 1)
  const isShopifyLegacyAccount = computed(() => isShopify.value && store.value!.customer_account_type === 1)

  const fetchStore = (companyId: number) => {
    useQuery({
      queryKey: ['fetchStore', companyId],
      queryFn: async () => {
        const [storeFetched] = (await useNuxtApp().$api(`/api/customers/stores/?company_id=${companyId}`)) as Store[]
        store.value = storeFetched
        return storeFetched ?? null
      },
      enabled: () => companyId !== 0
    })
  }

  const fetchStoreFromName = async (storeName: string) => {
    const [storeFetched] = (await useNuxtApp().$api(`/api/customers/stores/`, {
      params: { name: storeName }
    })) as Store[]
    store.value = storeFetched
    return storeFetched ?? null
  }

  const langList = ref<StoreLocales[] | null>([])

  const fetchLocales = async () => {
    const locales: StoreLocales[] = await useNuxtApp().$api(`/api/customers/locales/`)
    langList.value = locales
  }

  const isCurrentLangActive = computed((): boolean => {
    if (!langCode.value || !langList.value) return false

    return !!langList.value.find((lang) => lang.locale === langCode.value && lang.active)
  })

  const primaryLocale = computed(() => {
    if (!langList.value) return null

    const lang = langList.value.find((lang) => lang.primary)
    return lang ? lang.locale : null
  })

  const getPages = () => {
    return useQuery({
      queryKey: ['listPages'],
      queryFn: async (): Promise<StorePage[]> => {
        return await useNuxtApp().$api(`/api/customers/pages/`)
      },
      enabled: () => useStoreAuth().getJwtToken() !== null
    })
  }

  const addRewardToCart = (claimedRewardDetail: ClaimedRewardDetail) => {
    const { reward } = claimedRewardDetail

    if (
      ['BXGY', 'BXGYP', 'PRO'].includes(reward.promo_type) &&
      (reward.variant_ids?.length === 1 || reward?.variant_external_ids?.length === 1)
    ) {
      let variantId

      if (reward?.variant_external_ids?.length === 1) {
        variantId = reward?.variant_external_ids[0]
      } else if (reward.variant_ids?.length) {
        variantId = reward.variant_ids[0]
      }

      window.parent.postMessage(
        {
          variantId,
          addToCard: true,
          isWeWiink: true,
          promocode: claimedRewardDetail.code
        },
        '*'
      )

      return
    }
    window.parent.location = `${store.value!.url}/discount/${claimedRewardDetail.code}`
  }

  const embeddedViewportHeight = computed(() => {
    return viewportHeight.value - offset.value
  })

  return {
    store,
    isShopify,
    isShopifyLegacyAccount,
    fetchStore,
    fetchStoreFromName,
    getPages,
    addRewardToCart,
    scrollPosition,
    viewportHeight,
    appHeight,
    offset,
    embeddedViewportHeight,
    modalHeight,
    modalTop,
    langCode,
    currencyCodeExtension,
    currencyRate,
    currencyCode,
    isCurrentLangActive,
    fetchLocales,
    primaryLocale,
    langSuffix,
    origin
  }
})
